import { Stack } from '@mui/material';
import { isEmpty } from 'lodash';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Link01Icon } from '@/components/icons/Link01Icon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useIsAssetProviderIntegrationEnabled } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EstateOnboardingEntitySuggestionsButton } from '@/modules/estate/estateAIOnboarding/EstateOnboardingEntitySuggestionsButton';
import {
  EstatePage_EntityFragment,
  EstatePage_UnacknowledgedAiSuggestionFragment,
  EstatePage_UnacknowledgedHoldingSuggestionFragment,
} from '@/modules/estate/graphql/EstatePageData.generated';

export interface PrecreatedHoldingsGroupActionsProps {
  isIntegrationExcluded: boolean;
  holdingsSuggestions: EstatePage_UnacknowledgedHoldingSuggestionFragment[];
  entity: EstatePage_EntityFragment;
  /** Pre-computed V2 suggestions for this entity from the Estate context map. */
  entitySuggestionsV2?: EstatePage_UnacknowledgedAiSuggestionFragment[];
  onAddHoldingClick: () => void;
  onHoldingsSuggestionClick?: () => void;
  onLinkIntegrationClick: () => void;
  onEditEntitySectionSuggestionClick?: (section: EditEntitySection) => void;
}

export function PrecreatedHoldingsGroupActions({
  isIntegrationExcluded,
  holdingsSuggestions,
  entity,
  entitySuggestionsV2,
  onAddHoldingClick,
  onLinkIntegrationClick,
  onHoldingsSuggestionClick,
  onEditEntitySectionSuggestionClick,
  ...stackProps
}: PrecreatedHoldingsGroupActionsProps) {
  const canEdit = useCanEditHouseholdData();
  const canManageIntegrationData = useCanManageIntegrationData();
  const { canConnectToIntegrations } = useIsAssetProviderIntegrationEnabled();
  return (
    <Stack direction="row" {...stackProps} spacing={1}>
      {canEdit && (
        <EstateOnboardingEntitySuggestionsButton
          holdingsSuggestions={holdingsSuggestions}
          entity={entity}
          entitySuggestionsV2={entitySuggestionsV2}
          onHoldingsSuggestionClick={onHoldingsSuggestionClick}
          onEditEntitySectionSuggestionClick={
            onEditEntitySectionSuggestionClick
          }
        />
      )}
      {!isIntegrationExcluded &&
        canManageIntegrationData &&
        !isEmpty(canConnectToIntegrations) && (
          <Tooltip title="Link external data">
            <IconButton
              variant="transparent"
              size="xs"
              icon={Link01Icon}
              ariaLabel="Link data"
              onClick={(event) => {
                event.stopPropagation();
                onLinkIntegrationClick();
              }}
            />
          </Tooltip>
        )}
      {canEdit && (
        <Tooltip title="Add holdings">
          <IconButton
            data-testid="add-holdings-button"
            variant="transparent"
            size="xs"
            icon={PlusIcon}
            ariaLabel="Create direct holding"
            onClick={(event) => {
              event.stopPropagation();
              onAddHoldingClick();
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

export default PrecreatedHoldingsGroupActions;
